import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getApiUrl } from '@/lib/assistentusApi';
import { getFreshAccessToken } from '@/lib/authToken';

/**
 * Background-task event stream — app-level store.
 *
 * Previously the EventSource lived inside ChatInterface, so leaving the chat
 * route (e.g. to Notes) unmounted the subscription and lost every event that
 * arrived while the user was elsewhere. This store hoists the connection so
 * it stays open for the whole authenticated session, and persists the
 * rendered message timeline to localStorage so a hard reload also recovers.
 *
 * One EventSource per session — guarded by `connectingFor`.
 */

export interface BgMessage {
    id: string;            // stable per task lifecycle: "bg-<task_id>-start" / "-hb" / "-done" / "-failed"
    text: string;
    timestamp: number;     // epoch ms
}

interface BgStreamState {
    messages: BgMessage[];
    /** Вопрос Девуса появился или закрыт: чату перечитать сообщение. Не хранится. */
    devusSignal: { questionId: string; seq: number } | null;
    connectingFor: string | null;
    _es: EventSource | null;
    upsert: (id: string, text: string) => void;
    connect: (userId: string) => void | Promise<void>;
    disconnect: () => void;
    clear: () => void;
}

const MAX_MESSAGES = 100;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — older entries are dropped on load

export const useBgStreamStore = create<BgStreamState>()(
    persist(
        (set, get) => ({
            messages: [],
            devusSignal: null,
            connectingFor: null,
            _es: null,

            upsert: (id, text) => {
                set((state) => {
                    const ts = Date.now();
                    const idx = state.messages.findIndex((m) => m.id === id);
                    let next: BgMessage[];
                    if (idx === -1) {
                        next = [...state.messages, { id, text, timestamp: ts }];
                    } else {
                        next = state.messages.slice();
                        next[idx] = { id, text, timestamp: ts };
                    }
                    // Trim to MAX_MESSAGES newest entries.
                    if (next.length > MAX_MESSAGES) {
                        next = next.slice(next.length - MAX_MESSAGES);
                    }
                    return { messages: next };
                });
            },

            connect: async (userId) => {
                const state = get();
                if (!userId) return;
                // Idempotent — same user, existing connection: noop.
                if (state.connectingFor === userId && state._es) return;
                // Different user (logout/login) — tear down the old one first.
                if (state._es) {
                    try { state._es.close(); } catch { /* noop */ }
                }

                // Токен идёт параметром адреса: поток открывается средствами
                // браузера, а они заголовки слать не умеют. Без токена сервер
                // теперь отказывает — раньше чужой поток можно было слушать,
                // просто подставив чужой номер.
                // 🪤 Здесь зеркало особенно опасно: поток открывается на весь
                // сеанс, и пустой токен означает молчащий помощник до
                // перезагрузки. Спрашиваем живой пропуск.
                const token = (await getFreshAccessToken()) || '';
                const url = getApiUrl(
                    `/chat/bg-stream?user_id=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`,
                );
                const es = new EventSource(url);

                es.onmessage = async (e) => {
                    let ev: any;
                    try { ev = JSON.parse(e.data); } catch { return; }
                    if (!ev || !ev.type) return;

                    const tid = ev.task_id || '';
                    const agent = ev.agent || 'agent';
                    const upsert = get().upsert;

                    // Вопрос Девуса (карточка в самом сообщении чата): это не «фоновая
                    // задача», текста для ленты нет — только сигнал перечитать сообщение.
                    // Без этой ветки ниже запрашивался бы результат несуществующей задачи
                    // и в ленте появлялась пустая строка «завершён».
                    if (ev.kind === 'devus') {
                        if (ev.type === 'bg_task_completed') {
                            set((st) => ({ devusSignal: { questionId: String(tid), seq: (st.devusSignal?.seq ?? 0) + 1 } }));
                        }
                        return;
                    }

                    if (ev.type === 'bg_task_started') {
                        upsert(
                            `bg-${tid}-start`,
                            `🚀 *Запущен агент:* \`${agent}\`\n\n${ev.task || ''}`
                        );
                    } else if (ev.type === 'bg_task_heartbeat') {
                        const status = ev.status_message ? `\n_${ev.status_message}_` : '';
                        upsert(
                            `bg-${tid}-hb`,
                            `⏳ \`${agent}\` — ${ev.elapsed_min} мин в работе...${status}`
                        );
                    } else if (ev.type === 'bg_task_completed') {
                        try {
                            const res = await fetch(
                                getApiUrl(`/chat/bg-result/${tid}?user_id=${encodeURIComponent(userId)}`),
                                { headers: { 'Content-Type': 'application/json' } }
                            );
                            if (res.ok) {
                                const data = await res.json();
                                const body = data.result || ev.result_preview || '(пустой результат)';
                                const header = ev.is_final
                                    ? `✅ *Задача завершена:* \`${agent}\``
                                    : `✓ *${agent}* готов (промежуточный шаг)`;
                                upsert(`bg-${tid}-done`, `${header}\n\n${body}`);
                            } else {
                                const body = ev.result_preview || '';
                                upsert(`bg-${tid}-done`, `✅ *${agent}* завершён${body ? '\n\n' + body : ''}`);
                            }
                        } catch (err) {
                            console.error('bg-result fetch failed:', err);
                            const body = ev.result_preview || '';
                            upsert(`bg-${tid}-done`, `✅ *${agent}* завершён${body ? '\n\n' + body : ''}`);
                        }
                    } else if (ev.type === 'bg_task_failed') {
                        upsert(
                            `bg-${tid}-failed`,
                            `⚠️ *Задача не завершилась:* \`${agent}\`\n_${ev.reason || 'unknown'}_`
                        );
                    }
                };

                es.onerror = (e) => {
                    console.warn('[bgStream] error (EventSource will auto-retry):', e);
                };

                set({ _es: es, connectingFor: userId });
            },

            disconnect: () => {
                const es = get()._es;
                if (es) {
                    try { es.close(); } catch { /* noop */ }
                }
                set({ _es: null, connectingFor: null });
            },

            clear: () => set({ messages: [] }),
        }),
        {
            name: 'assistentus-bg-stream',
            storage: createJSONStorage(() => localStorage),
            // Only persist messages — the EventSource handle (`_es`) and
            // `connectingFor` are runtime-only.
            partialize: (state) => ({ messages: state.messages }),
            // Drop stale messages (older than 24h) on rehydrate.
            onRehydrateStorage: () => (state) => {
                if (!state) return;
                const cutoff = Date.now() - MAX_AGE_MS;
                state.messages = (state.messages || []).filter((m) => m.timestamp >= cutoff);
            },
        }
    )
);
